/**
 * PreviewUrlBar Component
 * 
 * A URL input bar with dropdown showing available pages/routes.
 * Similar to a browser address bar with route suggestions.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { PublicFile } from '../../types/AppPanel';

interface PreviewUrlBarProps {
  /** Base URL for the preview */
  baseUrl?: string;
  /** Available public files/pages */
  publicFiles?: PublicFile[];
  /** Currently selected path */
  currentPath?: string;
  /** Callback when path changes */
  onPathChange?: (path: string) => void;
  /** Callback to refresh preview */
  onRefresh?: () => void;
  /** Callback to open in new tab */
  onOpenExternal?: () => void;
}

export default function PreviewUrlBar({
  baseUrl,
  publicFiles,
  currentPath = '/',
  onPathChange,
  onRefresh,
  onOpenExternal,
}: PreviewUrlBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(currentPath);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Extract routes from public files
  const routes = useMemo(() => {
    if (!publicFiles || publicFiles.length === 0) {
      // Default route when no files detected
      return [{ path: '/', label: '/' }];
    }

    const routeSet = new Set<string>();
    routeSet.add('/'); // Always include root (index.html)

    publicFiles.forEach(file => {
      if (file.type === 'html' || file.name.endsWith('.html')) {
        // Convert file path to route - keep .html extension for non-index files
        // Parse Server static hosting requires the .html extension
        // public/index.html -> /
        // public/app.html -> /app.html
        // public/login.html -> /login.html
        // public/pages/about.html -> /pages/about.html
        let route = file.path
          .replace(/^public\/?/, '/');
        
        // Only index.html becomes / (root)
        if (route === '/index.html' || route.endsWith('/index.html')) {
          route = route.replace(/\/?index\.html$/, '/');
        }
        
        if (!route.startsWith('/')) route = '/' + route;
        // Clean up double slashes but keep single trailing slash for root
        route = route.replace(/\/+/g, '/');
        if (route !== '/' && route.endsWith('/')) {
          route = route.slice(0, -1);
        }
        
        routeSet.add(route);
      }
    });

    return Array.from(routeSet)
      .sort((a, b) => {
        if (a === '/') return -1;
        if (b === '/') return 1;
        return a.localeCompare(b);
      })
      .map(path => ({ 
        path, 
        // Show cleaner label (without .html) but use full path for navigation
        label: path === '/' ? '/' : path.replace(/\.html$/, '')
      }));
  }, [publicFiles]);

  // Update input when currentPath changes
  useEffect(() => {
    setInputValue(currentPath);
  }, [currentPath]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle route selection
  const handleSelectRoute = (path: string) => {
    setInputValue(path);
    setIsOpen(false);
    onPathChange?.(path);
  };

  // Handle input submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsOpen(false);
    onPathChange?.(inputValue);
  };

  return (
    <div ref={containerRef} className="relative flex-shrink min-w-0 w-full sm:w-[160px] md:w-[220px] lg:w-[300px] xl:w-[380px]">
      {/* URL Input Bar */}
      <form onSubmit={handleSubmit} className="flex items-center">
        <div className="
          flex items-center gap-2 w-full
          bg-white/5 border border-white/15 rounded-lg
          px-3 py-2 
          focus-within:border-white/20 focus-within:ring-1 focus-within:ring-[#208AEC]/30
          transition-all duration-200
        ">
          {/* Browser Icon */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white/40 flex-shrink-0">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M3 9H21" stroke="currentColor" strokeWidth="2"/>
            <circle cx="6.5" cy="6" r="1" fill="currentColor"/>
            <circle cx="9.5" cy="6" r="1" fill="currentColor"/>
          </svg>

          {/* Path Input */}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsOpen(true)}
            className="
              flex-1 bg-transparent text-sm text-white font-mono
              placeholder:text-white/30
              focus:outline-none
              min-w-0
            "
            placeholder="/"
          />

          {/* Open External Button */}
          {onOpenExternal && (
            <button
              type="button"
              onClick={onOpenExternal}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
              title="Open in new tab"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {/* Refresh Button */}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M1 4V10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 20V14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </form>

      {/* Routes Dropdown */}
      {isOpen && routes.length > 0 && (
        <div className="
          absolute top-full left-0 right-0 mt-1 z-[9999]
          bg-[#0c121c] backdrop-blur-xl
          border border-white/10 rounded-lg
          shadow-xl shadow-black/50
          max-h-[240px] overflow-y-auto
          py-1
        ">
          {routes.map(({ path, label }) => {
            const isActive = path === currentPath;
            return (
              <button
                key={path}
                type="button"
                onClick={() => handleSelectRoute(path)}
                className={`
                  w-full flex items-center justify-between gap-3
                  px-3 py-2.5 text-left
                  transition-colors duration-100
                  ${isActive
                    ? 'bg-[#208AEC]/25 text-white'
                    : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <span className="font-mono text-sm">{label}</span>
                {isActive && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#208AEC] flex-shrink-0">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

